import { useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Upload, FileSpreadsheet, Download, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useGet } from "../lib/hooks";
import { PageHeader, Pill } from "../components/Common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { CONSENT_BASES } from "./Contacts";

const TARGET_LABELS = {
  first_name: "First name", last_name: "Last name", full_name: "Full name (split)", email: "Email", phone: "Primary phone",
  phone_mobile: "Extra phone \u2014 Mobile", phone_work: "Extra phone \u2014 Work", phone_home: "Extra phone \u2014 Home", phone_other: "Extra phone \u2014 Other",
  company: "Company", website: "Website", address: "Street address", suburb: "Suburb", postcode: "Postcode", state: "State",
  tags: "Tags", source: "Source", notes: "Notes", consent_basis: "Consent basis", consent_date: "Consent date", consent_source: "Consent source",
};
const TEMPLATE = "First name,Last name,Email,Mobile,Work phone,Company,Website,Address,Suburb,State,Postcode,Tags,Consent basis,Consent date,Consent source,Notes\n" +
  "Jane,Citizen,jane@example.com.au,0400 000 000,02 9000 0000,Example Pty Ltd,https://example.com.au,1 George St,Sydney,NSW,2000,client;newsletter,express,2026-01-15,Signed up on website,\n";

function downloadTemplate() {
  const url = URL.createObjectURL(new Blob([TEMPLATE], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url; a.download = "bos-hq-contact-import-template.csv"; a.click();
  URL.revokeObjectURL(url);
}

const sel = "h-9 w-full rounded-md border border-input bg-background px-2 text-sm";

function MappingTable({ preview, mapping, setMapping }) {
  return (
    <div className="rounded-md border bg-card overflow-x-auto" data-testid="import-mapping">
      <table className="w-full text-sm">
        <thead><tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
          <th className="p-3">Column in your file</th><th className="p-3">Example</th><th className="p-3 w-72">Import into</th></tr></thead>
        <tbody>
          {preview.headers.map((h, i) => (
            <tr key={i} className="border-b last:border-0">
              <td className="p-3 font-medium">{h || <span className="text-muted-foreground">(blank)</span>}</td>
              <td className="p-3 text-muted-foreground truncate max-w-[260px]">{preview.sample[0]?.[i]}</td>
              <td className="p-3">
                <select className={sel} value={mapping[i] || ""} data-testid={`map-col-${i}`}
                  onChange={(e) => setMapping({ ...mapping, [i]: e.target.value })}>
                  <option value="">Don't import</option>
                  {preview.targets.map((t) => <option key={t} value={t}>{TARGET_LABELS[t] || t}</option>)}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Defaults({ d, setD, update, setUpdate }) {
  const set = (k, v) => setD({ ...d, [k]: v });
  return (
    <div className="rounded-md border bg-card p-5 space-y-4" data-testid="import-defaults">
      <div className="overline">Apply to every contact (when the file doesn't say)</div>
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="space-y-1.5 text-xs text-muted-foreground">Consent basis
          <select className={sel} value={d.consent_basis || ""} onChange={(e) => set("consent_basis", e.target.value)} data-testid="import-default-consent-basis">
            <option value="">Leave blank</option>{CONSENT_BASES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
          </select></label>
        <label className="space-y-1.5 text-xs text-muted-foreground">Consent date
          <Input type="date" value={d.consent_date || ""} onChange={(e) => set("consent_date", e.target.value)} data-testid="import-default-consent-date" /></label>
        <label className="space-y-1.5 text-xs text-muted-foreground">Consent source
          <Input value={d.consent_source || ""} placeholder="e.g. Imported from previous provider" onChange={(e) => set("consent_source", e.target.value)} data-testid="import-default-consent-source" /></label>
        <label className="space-y-1.5 text-xs text-muted-foreground">Add tags (comma separated)
          <Input value={(d.tags || []).join(",")} placeholder="imported" onChange={(e) => set("tags", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} data-testid="import-default-tags" /></label>
      </div>
      <label className="flex items-center gap-3 text-sm cursor-pointer">
        <input type="checkbox" className="h-4 w-4 accent-[#C2A462]" checked={update} onChange={(e) => setUpdate(e.target.checked)} data-testid="import-update-existing" />
        Update contacts that already exist (matched by email). Untick to skip them.
      </label>
      <p className="text-xs text-muted-foreground">People who previously unsubscribed stay opted out, whatever the file says.</p>
    </div>
  );
}

function History({ rows }) {
  if (!rows?.length) return null;
  return (
    <div className="rounded-md border bg-card p-5" data-testid="import-history">
      <div className="overline mb-3">Recent imports</div>
      {rows.map((r) => (
        <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 border-b last:border-0 py-2 text-sm">
          <span className="font-medium">{r.file_name}</span>
          <span className="text-muted-foreground text-xs">{r.created} created · {r.updated} updated · {r.skipped} skipped · {r.by} · {new Date(r.created_at).toLocaleString("en-AU")}</span>
        </div>
      ))}
    </div>
  );
}

export default function ContactImport({ embedded = false }) {
  const { user } = useAuth();
  if (user.role === "staff") return <Navigate to="/app/dashboard" replace />;
  return <ContactImportInner embedded={embedded} />;
}

function ContactImportInner({ embedded }) {
  const ref = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [d, setD] = useState({ consent_source: "", tags: ["imported"] });
  const [update, setUpdate] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [hist, reloadHist] = useGet("/contacts/import/history");

  const pick = async (f) => {
    if (!f) return;
    setFile(f); setResult(null); setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try { const r = await api.post("/contacts/import/preview", fd); setPreview(r.data); setMapping(r.data.mapping); }
    catch (e) { toast.error(errMsg(e)); setPreview(null); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const run = async () => {
    const m = Object.fromEntries(Object.entries(mapping).filter(([, v]) => v));
    const fd = new FormData();
    fd.append("file", file); fd.append("mapping", JSON.stringify(m)); fd.append("defaults", JSON.stringify(d)); fd.append("update_existing", String(update));
    setBusy(true);
    try { const r = await api.post("/contacts/import", fd); setResult(r.data); setPreview(null); reloadHist(); toast.success("Import complete"); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="contact-import-page">
      {embedded ? <div className="flex justify-end"><Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="download-template-btn"><Download className="h-4 w-4 mr-2" />Download template</Button></div>
      : <PageHeader overline="Admin" title="Contact Import" desc="Upload a CSV or Excel (.xlsx) spreadsheet of contacts. Match the columns, set the Spam Act consent basis, then import.">
        <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn"><Download className="h-4 w-4 mr-2" />Download template</Button>
      </PageHeader>}
      <input ref={ref} type="file" accept=".csv,.xlsx" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="import-file-input" />
      <button onClick={() => ref.current?.click()} disabled={busy} data-testid="import-dropzone"
        className="w-full rounded-md border border-dashed border-[#C2A462]/40 bg-card p-10 text-center transition-colors hover:border-[#C2A462] hover:bg-[#C2A462]/[0.04]">
        <FileSpreadsheet className="h-8 w-8 mx-auto text-[#C2A462]" strokeWidth={1.4} />
        <div className="mt-3 text-sm">{busy ? "Reading file\u2026" : file ? `Selected: ${file.name} \u2014 click to choose another` : "Click to choose a .csv or .xlsx file (max 5 MB, 5,000 contacts)"}</div>
      </button>
      {preview && (
        <>
          <div className="flex items-center gap-3 text-sm"><Pill tone="teal">{preview.total} contacts found</Pill><span className="text-muted-foreground">We matched what we could. Check each column below.</span></div>
          <MappingTable preview={preview} mapping={mapping} setMapping={setMapping} />
          <Defaults d={d} setD={setD} update={update} setUpdate={setUpdate} />
          <Button onClick={run} disabled={busy} data-testid="run-import-btn"><Upload className="h-4 w-4 mr-2" />{busy ? "Importing\u2026" : `Import ${preview.total} contacts`}</Button>
        </>
      )}
      {result && (
        <div className="rounded-md border border-[#C2A462]/40 bg-card p-5 space-y-2" data-testid="import-result">
          <div className="flex items-center gap-2 text-[#EBD6A0]"><CheckCircle2 className="h-5 w-5" />Import finished</div>
          <div className="text-sm" data-testid="import-result-counts">{result.created} created · {result.updated} updated · {result.skipped} skipped</div>
          {result.errors?.length > 0 && <div className="text-xs text-muted-foreground">{result.errors.slice(0, 10).map((e) => `Row ${e.row}: ${e.error}`).join(" \u00b7 ")}</div>}
        </div>
      )}
      <History rows={hist} />
    </div>
  );
}
